import { useState } from "react";
import { RESTORE_LIVE_CALL_ENABLED, type FeedSignal } from "../../types";
import { canRestoreSignal, restoreErrorMessage } from "../../restore";
import type { SignalActionMutations } from "../../hooks";

/**
 * Restore an archived signal (R-4).
 *
 * Mounted in the signal detail modal header, directly beneath the archive-state
 * line (ModalHeader.tsx) — the ruled surface. An earlier draft named
 * src/pages/Archive.tsx, but that page is a visual skeleton with no signal id in
 * scope and could not host a working control.
 *
 * Visibility is owner-or-admin on an archived signal (canRestoreSignal),
 * mirroring the server's requireAssignedSignal. Never rendered in the recipient
 * tier — ModalHeader gates that.
 *
 * Design-system baseline (R-UI-1): the same quiet popover + confirm pattern the
 * archive and reopen affordances already use — sx-btn / sx-popover / sx-cta. No
 * new visual language.
 */
export function RestoreControl({
  signal,
  viewerId,
  isAdmin,
  actions,
}: {
  signal: FeedSignal;
  viewerId: string;
  isAdmin: boolean;
  actions: SignalActionMutations;
}): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const restore = actions.restoreSignal;

  if (!canRestoreSignal(signal, viewerId, isAdmin)) return null;

  const close = () => {
    setOpen(false);
    restore.reset();
  };

  const message = restoreErrorMessage(restore.error);

  return (
    <div className="sx-correct" style={{ position: "relative", marginBottom: 8 }}>
      <button
        type="button"
        className="sx-btn sx-btn-p3 sx-btn-quiet"
        style={{ fontSize: 12 }}
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={!RESTORE_LIVE_CALL_ENABLED}
        title={
          RESTORE_LIVE_CALL_ENABLED
            ? undefined
            : "Restore turns on once the server update ships"
        }
        onClick={() => (open ? close() : setOpen(true))}
      >
        Restore
      </button>
      {open ? (
        <div
          className="sx-popover sx-popover--end"
          role="dialog"
          aria-label="Restore signal"
        >
          <div className="sx-popover-title">Restore this signal?</div>
          <p className="sx-popover-help">
            Restoring returns the signal to the active feed and clears the
            archive reason. The restore is recorded to the signal history.
          </p>
          {restore.isError ? (
            <p className="sx-popover-help sx-popover-help--error">{message}</p>
          ) : null}
          <div className="sx-popover-actions">
            <button type="button" className="sx-btn sx-btn-p3" onClick={close}>
              Cancel
            </button>
            <button
              type="button"
              className="sx-cta"
              disabled={restore.isPending}
              onClick={() => restore.mutate(undefined, { onSuccess: close })}
            >
              {restore.isPending ? "Restoring…" : "Restore signal"}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
